import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Quote } from "lucide-react";
import TeamCard from "@/components/TeamCard";
import SEO, { SITE_URL } from "@/components/SEO";
import { API } from "@/lib/apiBase";

export default function TeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        setTeam(response.data);
      } catch (error) {
        console.error("Error fetching team:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  const quoted = team.find((m) => m.quote);

  return (
    <div className="page-wrapper bg-ink" data-testid="team-page">
      <SEO
        title="Our Team — Senior Auckland Planners | Code Planning"
        description="Senior planners in Auckland with 40+ years combined experience — Haden Mills, Karyn Sands and Annalise Ogg. Every project run end-to-end by a senior planner on Auckland's North Shore."
        path="/team"
        keywords="auckland planners, planner auckland, north shore town planner, senior planner auckland, haden mills, karyn sands, annalise ogg, code planning team"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "Our Team — Code Planning Limited",
          url: `${SITE_URL}/team`,
          mainEntity: team.map((m) => ({
            "@type": "Person",
            name: m.name,
            jobTitle: m.role,
            description: m.bio,
            image: m.image_url,
            worksFor: { "@id": `${SITE_URL}/#organization` },
          })),
        }}
      />
      {/* Header */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
            Who We Are
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Our Team
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed">
            A small, senior planning team with deep Auckland roots. Senior planners on every project,
            with the experience to make resource consents straightforward.
          </p>
        </div>
      </section>

      {/* Team Grid (people first) */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
              {[...Array(3)].map((_, i) => (
                <div key={i}>
                  <div className="aspect-[3/4] bg-neutral-900 animate-pulse mb-6" />
                  <div className="h-8 bg-neutral-800 animate-pulse mb-4" />
                  <div className="h-4 bg-neutral-800 animate-pulse mb-2" />
                  <div className="h-20 bg-neutral-800 animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 items-start">
              {team.map((member) => (
                <TeamCard key={member.id} member={member} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Director Quote (below the team) */}
      {quoted && (
        <section className="py-10 md:py-14 bg-neutral-900 border-t border-neutral-800">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <p className="text-sm uppercase tracking-widest text-gray-400 mb-6">
              From the Director
            </p>
            <Quote className="w-10 h-10 text-neutral-700 mb-6" />
            <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl leading-snug text-white font-medium mb-8">
              {quoted.quote}
            </blockquote>
            <div className="flex items-center gap-4">
              <div className="h-px w-12 bg-neutral-600" />
              <p className="text-sm uppercase tracking-widest text-gray-400">
                {quoted.name} — {quoted.role}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Values */}
      <section className="py-10 md:py-14 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8 md:mb-10">
            <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
              What Drives Us
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white">
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Expertise",
                description:
                  "Deep knowledge of New Zealand's planning framework, built over decades of practice across all sectors.",
              },
              {
                title: "Clarity",
                description:
                  "We translate complex planning matters into clear, actionable advice. No jargon, no confusion.",
              },
              {
                title: "Results",
                description:
                  "We're focused on achieving outcomes. Our 99% success rate speaks to our strategic approach.",
              },
            ].map((value, index) => (
              <div key={index} className="border border-neutral-800 bg-ink p-8">
                <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to work together?
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Request a Free Property Planning Review with one of our senior planners.
          </p>
          <Link to="/contact#property-form" className="btn-primary inline-block" data-testid="team-cta">
            Check My Property
          </Link>
        </div>
      </section>
    </div>
  );
}
